import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { setAppointmentsListAppend, setAppointmentsTotalCount } from '../../store/appointmentsSlice';
import TableHeader from '../BaseComponents/TableHeader';
import AppointmentsTableRow from './AppointmentsTableRow';
import Loader from '../BaseComponents/Loader';
import { getAllAppointmentsList, GlobalAppointmentsFilters, GlobalAppointmentsApiFilters } from '../../api/appointments';
import { getDoctorsList } from '../../api/doctors';
import { getNutritionistsList } from '../../api/nutritionist';
import './Appointments.scss';
import './GlobalAppointmentsTable.scss';

const headerColumns = [
  { title: 'Appointment status', key: 'status', minWidth: '176px', flex: 0 },
  { title: 'Member', key: 'member', minWidth: '150px', flex: 0 },
  { title: 'Date', key: 'date', minWidth: '104px', flex: 0 },
  { title: 'Time', key: 'time', minWidth: '104px', flex: 0 },
  { title: 'Appointment name', key: 'appointmentName', minWidth: '170px', flex: 0 },
  { title: 'Assignee', key: 'assignee', minWidth: '160px', flex: 0 },
  { title: 'Membership Type', key: 'membershipType', minWidth: '150px', flex: 0 },
  { title: 'Location', key: 'location', minWidth: '140px', flex: 0 },
  { title: 'Appointment ID', key: 'appointmentId', minWidth: '140px', flex: 0 },
  { title: 'Result', key: 'result', minWidth: '126px', flex: 0 },
  { title: '', key: 'actions', minWidth: '48px', flex: 0 }
];

interface IProps {
  filter: GlobalAppointmentsFilters;
  apiFilters?: GlobalAppointmentsApiFilters;
  isLoading: boolean;
}

const AppointmentsList: React.FC<IProps> = ({ filter, apiFilters, isLoading }) => {
  const dispatch = useDispatch();
  const { appointmentsList, limit } = useSelector(
    (state: RootState) => state.appointments
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const offset = useRef(0);
  const loadingRef = useRef(false);
  const filterRef = useRef(filter);
  const apiFiltersRef = useRef(apiFilters);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [hasMore, setHasMore] = React.useState(true);
  const [doctorNameById, setDoctorNameById] = React.useState<Record<string, string>>(
    {}
  );
  const [nutritionistNameById, setNutritionistNameById] = React.useState<
    Record<string, string>
  >({});

  useEffect(() => {
    filterRef.current = filter;
    apiFiltersRef.current = apiFilters;
    offset.current = 0;
    setHasMore(true);
  }, [filter, apiFilters]);

  useEffect(() => {
    let cancelled = false;
    const fetchAssignees = async () => {
      try {
        const [doctorsRes, nutritionistsRes] = await Promise.all([
          getDoctorsList(0, 1000),
          getNutritionistsList(0, 1000),
        ]);

        if (cancelled) return;

        const doctorsMap: Record<string, string> = {};
        (doctorsRes.doctors || []).forEach((d: any) => {
          const id = d?.user_id ?? d?.id;
          const name = `${d?.first_name || ''} ${d?.last_name || ''}`.trim();
          if (id && name) doctorsMap[String(id)] = name;
        });
        setDoctorNameById(doctorsMap);

        const nutritionistsMap: Record<string, string> = {};
        (nutritionistsRes.nutritionists || []).forEach((n: any) => {
          const id = n?.user_id ?? n?.id;
          const name = `${n?.first_name || ''} ${n?.last_name || ''}`.trim();
          if (id && name) nutritionistsMap[String(id)] = name;
        });
        setNutritionistNameById(nutritionistsMap);
      } catch {
        // Non-fatal: table can still render without names
      }
    };
    fetchAssignees();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadMoreAppointments = useCallback(async () => {
    if (loadingRef.current || !hasMore) return;
    
    loadingRef.current = true;
    offset.current += limit;

    try {
      const res = await getAllAppointmentsList(
        limit,
        offset.current,
        filterRef.current.filterType,
        apiFiltersRef.current
      );
      if (res.appointments.length > 0) {
        dispatch(setAppointmentsListAppend(res.appointments));
        dispatch(setAppointmentsTotalCount(res.total_count ?? 0));
        setHasMore(res.appointments.length >= limit);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('fetchAppointments error :>> ', err);
    } finally {
      loadingRef.current = false;
    }
  }, [dispatch, hasMore, limit]);

  const handleScroll = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      const el = scrollContainerRef.current;
      if (!el || loadingRef.current || !hasMore) return;

      const { scrollTop, scrollHeight, clientHeight } = el;
      const threshold = 100;

      if (scrollTop + clientHeight >= scrollHeight - threshold) {
        loadMoreAppointments();
      }
    }, 150);
  }, [hasMore, loadMoreAppointments]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [handleScroll]);

  return (
    <div className='scroll-container global-appointments-table'>
      <div className='table-container'>
        <TableHeader headerColumns={headerColumns} />
        <div className='member-table-body' ref={scrollContainerRef}>
          {isLoading && <Loader />}
          {appointmentsList &&
            !isLoading &&
            appointmentsList.map((appointment) => (
              <AppointmentsTableRow
                key={appointment.id}
                appointmentData={appointment}
                doctorNameById={doctorNameById}
                nutritionistNameById={nutritionistNameById}
              />
            ))}
        </div>
      </div>
    </div>
  );
};

export default AppointmentsList;
